import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import * as Yup from 'yup';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import {
  ContactAttemptDto,
  ContactAttemptOutcome,
  TypeContactAttempt,
} from 'src/crs/dto/contact-attempt.dto';
import { MobileDatePicker } from '@mui/lab';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { yupResolver } from '@hookform/resolvers/yup';
import { useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { convertValueToValueSet } from 'src/careflow/common/common-utils';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import CancelButton from 'src/careflow/common/CancelButton';
import DialogCloseButton, { useUnsavedCloseGuard } from 'src/components/DialogCloseButton';
import moment from 'moment';
import { useMemo } from 'react';
import jsonpath from 'jsonpath';

type Props = {
  option: string;
  referral: any;
  open: boolean;
  countRecord: any;
  onCancel: VoidFunction;
  handleCreateContactForm: (data: any) => Promise<any>;
  checkListData: any;
};

export default function ContactForm({
  option,
  referral,
  open,
  countRecord,
  handleCreateContactForm,
  onCancel,
  checkListData,
}: Props) {
  const isContactForm = checkListData?.code?.includes('CONTACT_ATTEMPTS_CARE_PLAN_ITEM');

  const countAttemptsFromConditions: string | null = useMemo(() => {
    if (!checkListData && !isContactForm) {
      return null;
    }

    try {
      const conditions = JSON.parse(checkListData.conditions);
      const attempts = conditions ? jsonpath.value(conditions, `$..attempts`) : null;

      return attempts;
    } catch (err) {
      return null;
    }
  }, [checkListData]);

  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useLocales();

  const [outcomeList] = useValueSet('ph-outcome-contact-form', { map: ValueSetWrapper });

  type FormValues = {
    contactOn: string;
    nextContactOn: string;
    outcome: string;
    reasonNote: string;
  };

  const EventSchema = Yup.object().shape({
    contactOn: Yup.date()
      .required('Contact On is required')
      .nullable()
      .transform((curr, orig) => (moment(orig).isValid() ? curr : null)),
    nextContactOn: Yup.date()
      .nullable()
      .transform((curr, orig) => (moment(orig).isValid() ? curr : null))
      .when('contactOn', (contactOn: Date | null, schema: any) => {
        if (contactOn) {
          return schema.min(contactOn, "Next Contact On can't be earlier than Contact On");
        }
        return schema;
      }),
    outcome: Yup.string().required('Outcome is required'),
    reasonNote: Yup.string(),
  });

  const defaultValues = {
    contactOn: '',
    nextContactOn: '',
    outcome: '',
    reasonNote: '',
  };

  const methods = useForm({
    resolver: yupResolver(EventSchema),
    defaultValues,
  });

  const { control, handleSubmit, reset } = methods;

  const mapFormDataToCommunication = (data: FormValues): ContactAttemptDto => {
    const { contactOn, nextContactOn, outcome, reasonNote } = data;

    const topicCoding = convertValueToValueSet(outcome, outcomeList);

    return {
      typeNote: TypeContactAttempt.CASE_CONTACT_ITEM,
      reasonNote: reasonNote,
      outcome: topicCoding?.display as ContactAttemptOutcome,
      id: Number(referral.caseId),
      contactOn: new Date(contactOn).toISOString(),
      ...(nextContactOn && { nextContactOn: new Date(nextContactOn).toISOString() }),
    };
  };

  const handleClose = () => {
    reset(defaultValues);
    onCancel();
  };

  const requestClose = useUnsavedCloseGuard(handleClose, methods.formState.isDirty);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const resultMap = mapFormDataToCommunication(data);
      const result = await handleCreateContactForm(resultMap);
      handleClose();
      if (!isEmpty(result)) enqueueSnackbar('Register was created.');
    } catch (e) {
      handleClose();
      enqueueSnackbar('Register was not created.', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={() => void requestClose()} fullWidth={true} maxWidth="sm">
      <DialogCloseButton onClose={handleClose} hasUnsavedChanges={methods.formState.isDirty} />
      <DialogTitle>
        {option}&nbsp;
        {countRecord !== undefined && countAttemptsFromConditions
          ? `[${countRecord}/${countAttemptsFromConditions}]`
          : ''}
      </DialogTitle>

      <Card sx={{ m: 2 }}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container>
            <Grid item xs={12} md={12}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <Controller
                  name="contactOn"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <MobileDatePicker
                      {...field}
                      label={`${i18n('case.details.contactAttempts.contactOn', 'crs')}*`}
                      inputFormat="MM/dd/yyyy"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  name="nextContactOn"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <MobileDatePicker
                      {...field}
                      label={i18n('case.details.contactAttempts.nextContactOn', 'crs')}
                      inputFormat="MM/dd/yyyy"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  )}
                />

                <RHFSelect
                  name="outcome"
                  label={`${i18n('case.details.contactAttempts.outcome', 'crs')}*`}
                >
                  <MenuItem />
                  {outcomeList?.asList().map((option: any) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.display}
                    </MenuItem>
                  ))}
                </RHFSelect>

                <RHFTextField
                  name="reasonNote"
                  label={i18n('case.details.contactAttempts.reasonNote', 'crs')}
                  multiline
                  rows={4}
                />
              </Stack>
            </Grid>
          </Grid>

          <Stack spacing={2} alignItems="center">
            <DialogActions>
              <Box sx={{ flexGrow: 1 }} />

              <CancelButton onClick={() => void requestClose()}>{i18n('cancel')}</CancelButton>
              <Button variant="contained" type="submit">
                {i18n('saveChanges')}
              </Button>
            </DialogActions>
          </Stack>
        </FormProvider>
      </Card>
    </Dialog>
  );
}
